import React, { useCallback, useEffect, useState } from 'react';
import {
    Drawer,
    Descriptions,
    Card,
    Tabs,
    Table,
    DatePicker,
    Button,
    message,
    Spin,
    Tag,
    Empty,
    Tooltip,
} from 'antd';
import moment from 'moment';

import {
    getGymOrderDetails,
    getGymOrderBookings,
} from '../../services/api/gym-enrolments';
import {
    healthBadge,
    statusBadge,
    partnerCallStatusBadge,
    failureCodeLabel,
    sourceBadge,
} from './common/badges';

const { TabPane } = Tabs;

/**
 * Slide-in drawer with order/member details + diagnostics + audit.
 *
 * `groupKey` controls visibility — pass null to close. Read-only for
 * now: action buttons are intentionally hidden until the backend
 * actions are re-enabled.
 */
const OrderDetailsDrawer = ({ groupKey, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState(null);
    const [bookingsLoading, setBookingsLoading] = useState(false);

    const reload = useCallback(() => {
        if (!groupKey) {
            setData(null);
            return;
        }
        setLoading(true);
        getGymOrderDetails(groupKey)
            .then(setData)
            .catch((err) => {
                message.error(err?.message || 'Failed to load order details');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [groupKey]);

    useEffect(() => {
        reload();
    }, [reload]);

    const loadBookings = useCallback(() => {
        if (!groupKey || bookings !== null) return;
        setBookingsLoading(true);
        getGymOrderBookings(groupKey)
            .then((d) => setBookings(d?.bookings ?? []))
            .catch((err) => {
                message.error(err?.message || 'Failed to load bookings');
                setBookings([]);
            })
            .finally(() => setBookingsLoading(false));
    }, [groupKey, bookings]);

    // Reset bookings cache whenever the drawer is opened on a new group.
    useEffect(() => {
        setBookings(null);
    }, [groupKey]);

    return (
        <Drawer
            className="gym-ops-shell"
            open={Boolean(groupKey)}
            title={
                groupKey ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                        <span className="text-base font-semibold">
                            {groupKey.startsWith('DIRECT:')
                                ? 'Direct enrolment'
                                : `Order ${groupKey}`}
                        </span>
                        {data && (
                            <span className="text-xs text-gray-500">
                                {data.partner} · {data.members?.length || 0}{' '}
                                member(s)
                            </span>
                        )}
                    </div>
                ) : (
                    'Loading…'
                )
            }
            onClose={onClose}
            width={Math.min(window.innerWidth - 16, 880)}
            destroyOnClose
            bodyStyle={{ padding: 16 }}
        >
            {loading && (
                <div className="py-12 flex justify-center">
                    <Spin />
                </div>
            )}

            {!loading && !data && <Empty description="No data" />}

            {!loading && data && (
                <Tabs
                    defaultActiveKey="overview"
                    onChange={(key) => {
                        if (key === 'bookings') loadBookings();
                    }}
                >
                    <TabPane tab="Overview" key="overview">
                        <OverviewPane data={data} />
                    </TabPane>
                    <TabPane
                        tab={`Members (${data.members?.length || 0})`}
                        key="members"
                    >
                        <MembersPane data={data} />
                    </TabPane>
                    <TabPane
                        tab={
                            bookings !== null
                                ? `Bookings (${bookings.length})`
                                : 'Bookings'
                        }
                        key="bookings"
                    >
                        <BookingsPane
                            loading={bookingsLoading}
                            bookings={bookings}
                        />
                    </TabPane>
                </Tabs>
            )}
        </Drawer>
    );
};

const OverviewPane = ({ data }) => (
    <div className="space-y-4">
        <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 1, md: 2 }}
            labelStyle={{ fontWeight: 500, width: 130 }}
        >
            <Descriptions.Item label="Source">
                {sourceBadge(data.sourceType)}
            </Descriptions.Item>
            <Descriptions.Item label="Partner">{data.partner}</Descriptions.Item>
            <Descriptions.Item label="Holder">
                {data.holder?.name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
                {data.holder?.phone || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
                {data.holder?.email || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Sponsor">
                {data.holder?.sponsorName || `#${data.holder?.sponsorId}`}
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
                {data.plan?.title || `#${data.plan?.planId}`}
            </Descriptions.Item>
            <Descriptions.Item label="Partner SKU">
                {data.plan?.partnerPlanIdSnapshot || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
                {data.plan?.durationMonths} months
            </Descriptions.Item>
            <Descriptions.Item label="Times/week">
                {data.plan?.timesPerWeek}
            </Descriptions.Item>
        </Descriptions>
    </div>
);

const MembersPane = ({ data }) => (
    <div className="space-y-3">
        {(data.members || []).map((m) => (
            <MemberCard key={m.enrolmentId} member={m} />
        ))}
    </div>
);

const MemberCard = ({ member }) => {
    const hasIssues = (member.failureCodes || []).length > 0;
    return (
        <Card
            size="small"
            className="border border-gray-200"
            bodyStyle={{ padding: 12 }}
        >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div>
                    <div className="text-sm font-semibold">
                        {member.memberName || 'Self'}{' '}
                        <span className="text-xs text-gray-500">
                            (enrolment #{member.enrolmentId})
                        </span>
                    </div>
                    <div className="text-xs text-gray-500">
                        Created{' '}
                        {member.createdAt
                            ? moment(member.createdAt).format(
                                  'DD MMM YYYY HH:mm',
                              )
                            : '—'}{' '}
                        · last update{' '}
                        {member.updatedAt
                            ? moment(member.updatedAt).fromNow()
                            : '—'}
                    </div>
                </div>
                <div className="flex flex-wrap gap-1">
                    {statusBadge(member.status)}
                    {partnerCallStatusBadge(member.partnerCallStatus)}
                    {healthBadge(member.healthState)}
                </div>
            </div>

            <Descriptions
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
                className="mt-3"
                labelStyle={{ fontWeight: 500 }}
            >
                <Descriptions.Item label="Total">
                    ₹{Number(member.total || 0).toLocaleString('en-IN')}
                </Descriptions.Item>
                <Descriptions.Item label="Wallet">
                    ₹{Number(member.walletDeduction || 0).toLocaleString('en-IN')}
                </Descriptions.Item>
                <Descriptions.Item label="Copay">
                    ₹{Number(member.copay || 0).toLocaleString('en-IN')}
                </Descriptions.Item>
                <Descriptions.Item label="Partner member id">
                    {member.partnerMemberId || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="dpu_id">
                    {member.dopePolicyUserId ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Validity">
                    {member.startsAt || '—'} → {member.endsAt || '—'}
                </Descriptions.Item>
            </Descriptions>

            {member.partnerFailureReason && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2">
                    Partner reason: {member.partnerFailureReason}
                </div>
            )}

            {hasIssues && (
                <div className="mt-3 border-t pt-2">
                    <div className="text-xs font-semibold text-gray-700 mb-1">
                        Diagnostics
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {member.failureCodes.map((code) => (
                            <Tooltip
                                key={code}
                                title={failureCodeLabel(code)}
                                placement="top"
                            >
                                <Tag color="red" className="cursor-help">
                                    {code}: {failureCodeLabel(code)}
                                </Tag>
                            </Tooltip>
                        ))}
                    </div>
                </div>
            )}

        </Card>
    );
};

const BOOKING_STATUS_COLOR = {
    success: 'green',
    pending: 'gold',
    inPreBooking: 'processing',
    cancelled: 'default',
};

const formatBookingWindow = (b) => {
    if (b.epochStartTime && b.epochEndTime) {
        const start = moment.unix(Number(b.epochStartTime));
        const end = moment.unix(Number(b.epochEndTime));
        return `${start.format('DD MMM YYYY HH:mm')} – ${end.format('HH:mm')}`;
    }
    if (b.scheduleDate) {
        return `${b.scheduleDate}${b.scheduleSlot ? ` · ${b.scheduleSlot}` : ''}`;
    }
    return '—';
};

const bookingDateValue = (b) => {
    if (b.epochStartTime) {
        return moment.unix(Number(b.epochStartTime));
    }
    if (b.scheduleDate) {
        return moment(b.scheduleDate, 'YYYY-MM-DD');
    }
    if (b.createdAt) {
        return moment(b.createdAt);
    }
    return null;
};

const BookingsPane = ({ loading, bookings }) => {
    const [dateRange, setDateRange] = useState(null);

    const filtered = React.useMemo(() => {
        if (!bookings) return [];
        if (!dateRange || !dateRange[0] || !dateRange[1]) return bookings;
        const start = dateRange[0].clone().startOf('day');
        const end = dateRange[1].clone().endOf('day');
        return bookings.filter((b) => {
            const d = bookingDateValue(b);
            return d && d.isBetween(start, end, undefined, '[]');
        });
    }, [bookings, dateRange]);

    if (loading) {
        return (
            <div className="py-8 flex justify-center">
                <Spin />
            </div>
        );
    }
    if (!bookings || bookings.length === 0) {
        return <Empty description="No bookings for this user yet" />;
    }
    const columns = [
        {
            title: 'When',
            key: 'when',
            render: (_, r) => (
                <div className="leading-tight">
                    <div className="text-sm">{formatBookingWindow(r)}</div>
                    {r.cancelledAt && (
                        <div className="text-xs text-gray-500">
                            cancelled {moment(r.cancelledAt).fromNow()}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Center',
            key: 'center',
            render: (_, r) => (
                <Tooltip title={r.centerAddress || ''}>
                    <div className="leading-tight">
                        <div className="text-sm font-medium truncate">
                            {r.centerName || '—'}
                        </div>
                        {r.centerAddress && (
                            <div
                                className="text-xs text-gray-500 truncate"
                                style={{ maxWidth: 260 }}
                            >
                                {r.centerAddress}
                            </div>
                        )}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Activity',
            dataIndex: 'activityName',
            width: 140,
            ellipsis: true,
            render: (v) => v || '—',
        },
        {
            title: 'User',
            dataIndex: 'userId',
            width: 90,
            align: 'right',
            render: (v) => `#${v}`,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (s) => (
                <Tag color={BOOKING_STATUS_COLOR[s] || 'default'}>
                    {s || '—'}
                </Tag>
            ),
        },
    ];
    return (
        <div>
            <div
                className="flex items-center gap-2 flex-wrap mb-3"
                style={{ fontSize: 12 }}
            >
                <span className="text-gray-500">Filter by date:</span>
                <DatePicker.RangePicker
                    size="small"
                    value={dateRange}
                    onChange={setDateRange}
                    format="DD MMM YYYY"
                    allowClear
                />
                {dateRange && (
                    <Button size="small" onClick={() => setDateRange(null)}>
                        Clear
                    </Button>
                )}
                <span className="text-gray-500 ml-auto">
                    {filtered.length} of {bookings.length}
                </span>
            </div>
            <Table
                rowKey="bookingId"
                columns={columns}
                dataSource={filtered}
                size="small"
                pagination={
                    filtered.length > 20
                        ? { pageSize: 20, size: 'small', showSizeChanger: false }
                        : false
                }
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No bookings in this date range"
                        />
                    ),
                }}
            />
        </div>
    );
};

export default OrderDetailsDrawer;
